import clsx from 'clsx';
import type { ReactNode } from 'react';
import { BiCheck } from 'react-icons/bi';
import { GoPlus } from 'react-icons/go';

import styles from './SelectionChip.module.scss';

/** `alert` and `caution` paint what someone must not be served: an allergy, a dietary exclusion. */
type SelectionChipVariant = 'default' | 'primary' | 'secondary' | 'alert' | 'caution';
type SelectionChipSize = 'sm' | 'md' | 'lg';

interface SelectionChipProps {
  label: string;
  onRemove?: () => void;
  variant?: SelectionChipVariant;
  size?: SelectionChipSize;
  /** A glyph before the label. Decorative: the label already says what it is. */
  icon?: ReactNode;
  /**
   * Makes the chip a toggle: a button that shows the variant's fill and a check while
   * `selected`, and a plain outline while not. Without it the chip always shows its fill.
   */
  onToggle?: () => void;
  selected?: boolean;
  className?: string;
}

/**
 * A chosen value as a chip, with an optional cross to remove it, or, given `onToggle`,
 * one option of a multiple choice that is picked by pressing it.
 */
const SelectionChip: React.FC<SelectionChipProps> = ({
  label,
  onRemove,
  variant = 'default',
  size = 'md',
  icon,
  onToggle,
  selected = false,
  className,
}) => {
  const variantClass = {
    default: styles.variantDefault,
    primary: styles.variantPrimary,
    secondary: styles.variantSecondary,
    alert: styles.variantAlert,
    caution: styles.variantCaution,
  }[variant];

  const sizeClass = {
    sm: styles.sizeSm,
    md: styles.sizeMd,
    lg: styles.sizeLg,
  }[size];

  const content = (
    <>
      {icon && (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles.label}>{label}</span>
    </>
  );

  if (onToggle) {
    return (
      <button
        type="button"
        className={clsx(
          styles.chip,
          styles.toggle,
          sizeClass,
          selected ? variantClass : styles.unselected,
          className,
        )}
        aria-pressed={selected}
        onClick={onToggle}
      >
        {content}
        {selected && (
          <span className={styles.check} aria-hidden="true">
            <BiCheck />
          </span>
        )}
      </button>
    );
  }

  return (
    <div className={clsx(styles.chip, variantClass, sizeClass, className)}>
      {content}
      {onRemove && (
        <button
          type="button"
          className={styles.removeButton}
          onClick={onRemove}
          aria-label={`Remove ${label}`}
        >
          <GoPlus className={styles.removeIcon} />
        </button>
      )}
    </div>
  );
};

export default SelectionChip;
